import { RecordingSource } from './types'

const DB_NAME = 'AudioRecorderDB'
const STORE_NAME = 'AudioChunks'
const DB_VERSION = 14

export interface AudioChunkRecord {
  interviewId: string
  chunk: Blob
  timestamp: number
  sequence: number
  source: RecordingSource
  mimeType: string
  recordingStartTime: number
}

export type AudioChunkData = {
  chunks: Blob[]
  source: RecordingSource
  mimeType: string
  recordingStartTime: number
}

export const initDB = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result)

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result

      if (db.objectStoreNames.contains(STORE_NAME)) {
        db.deleteObjectStore(STORE_NAME)
      }

      const store = db.createObjectStore(STORE_NAME, {
        keyPath: ['interviewId', 'sequence'],
      })

      store.createIndex('byInterviewId', 'interviewId', { unique: false })
    }
  })

export async function saveAudioChunk(
  interviewId: string,
  chunk: Blob,
  sequence: number,
  source: RecordingSource,
  mimeType: string,
  recordingStartTime: number,
): Promise<void> {
  const db = await initDB()

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)

    const record: AudioChunkRecord = {
      interviewId,
      chunk,
      timestamp: Date.now(),
      sequence,
      source,
      mimeType,
      recordingStartTime,
    }

    const request = store.put(record)
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

export async function getAudioChunks(
  interviewId: string,
): Promise<AudioChunkData | null> {
  const db = await initDB()

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const store = tx.objectStore(STORE_NAME)
    const index = store.index('byInterviewId')
    const request = index.getAll(IDBKeyRange.only(interviewId))

    request.onsuccess = () => {
      const records = request.result as AudioChunkRecord[]
      if (records.length === 0) {
        resolve(null)
        return
      }

      records.sort((a, b) => a.sequence - b.sequence)
      const chunks = records.map((r) => r.chunk)
      const { source, mimeType, recordingStartTime } = records[0]

      resolve({ chunks, source, mimeType, recordingStartTime })
    }

    request.onerror = () => reject(request.error)
  })
}

export async function deleteAudioChunks(interviewId?: string): Promise<void> {
  const db = await initDB()

  if (!interviewId) {
    return
  }

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)

    const index = store.index('byInterviewId')
    const cursorReq = index.openKeyCursor(IDBKeyRange.only(interviewId))

    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result
      if (cursor) {
        store.delete(cursor.primaryKey)
        cursor.continue()
      } else {
        resolve()
      }
    }

    cursorReq.onerror = () => reject(cursorReq.error)
  })
}

export async function getLastSequenceNumber(
  interviewId: string,
): Promise<number> {
  const db = await initDB()

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const store = tx.objectStore(STORE_NAME)
    const index = store.index('byInterviewId')
    const request = index.getAllKeys(IDBKeyRange.only(interviewId))

    request.onsuccess = () => {
      const keys = request.result as [string, number][]
      if (keys.length === 0) {
        resolve(-1)
        return
      }
      const maxSequence = Math.max(...keys.map(([, seq]) => seq))
      resolve(maxSequence)
    }

    request.onerror = () => reject(request.error)
  })
}
